// src/app/components/NewLayout/NewLayout.jsx
import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./NewLayout.scss";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import logo from "../../assets/images/newLogo.png";
import { UserContext } from "../../store/UserContext";
import UserAvatar from "../UserAvatar/UserAvatar";
import Spinner from "../Spinner/Spinner";
import { getLoginType, getAccessToken } from "../../services/auth";
import { logoutAndRedirect } from "../../utils/apiCalls/auth";

// MUI Components for modern dropdown, tooltips, and profile pill
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";

// MUI Icons for sidebar navigation tabs
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import WatchIcon from "@mui/icons-material/Watch";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import PollIcon from "@mui/icons-material/Poll";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

const superAdminMenu = [
  { to: "/admin-dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  {
    to: "/hptable",
    label: "Healthcare Professional",
    icon: <MedicalServicesIcon />,
    activePaths: [
      "/hpform",
      "/superadmin-patients",
      "/superadmin-session",
      "/superadmin-graph",
    ],
  },
  {
    to: "/add-device",
    label: "Devices",
    icon: <WatchIcon />,
    activePaths: ["/device-data"],
  },
  { to: "/logs", label: "Logs", icon: <AssignmentIcon /> },
  { to: "/app-versions", label: "App Versions", icon: <SystemUpdateAltIcon /> },
  {
    to: "/profile-details",
    label: "Profile",
    icon: <PersonIcon />,
    activePaths: ["/superadmin-edit"],
  },
];

const patientMenu = [
  {
    to: "/hp-dashboard",
    label: "Dashboard",
    icon: <DashboardIcon />,
  },
  {
    to: "/patient-table",
    label: "Patients",
    icon: <PeopleIcon />,
    activePaths: ["/patient-form", "/patient-view", "/session-graph"],
  },
  {
    to: "/surveys",
    label: "Surveys",
    icon: <PollIcon />,
    activePaths: ["/survey-form", "/survey-results", "/survey-results-view"],
  },
  { to: "/my-devices", label: "Devices", icon: <WatchIcon /> },
  {
    to: "/patient-logs",
    label: "Logs",
    icon: <AssignmentIcon />,
    activePaths: ["/user-activity-logs", "/device-activity-logs"],
  },
  {
    to: "/profile-details",
    label: "Profile",
    icon: <PersonIcon />,
    activePaths: ["/user-form"],
  },
];

const getPageTitle = (pathname) => {
  if (pathname.includes("dashboard")) return "Dashboard";
  if (pathname.includes("hptable") || pathname.includes("hpform"))
    return "Healthcare Professionals";
  if (pathname.includes("logs") || pathname.includes("patient-logs"))
    return "Activity Logs";
  if (pathname.includes("app-versions")) return "App Versions";
  if (pathname.includes("device-data")) return "Devices Data";
  if (
    pathname.includes("device") ||
    pathname.includes("add-device") ||
    pathname.includes("my-devices")
  )
    return "Devices";
  if (pathname.includes("patient-table") || pathname.includes("patient-form"))
    return "Patients";
  if (pathname.includes("survey-results")) return "Survey Results";
  if (pathname.includes("survey-form")) return "Survey Builder";
  if (pathname.includes("surveys")) return "Surveys";
  if (pathname.includes("user-form") || pathname.includes("superadmin-edit"))
    return "Edit Profile";
  if (pathname.includes("profile")) return "Profile Details";
  if (pathname.includes("apikeylist")) return "API Key Management";
  if (pathname.includes("patient-view")) return "Patient Details";
  if (pathname.includes("session-graph")) return "Session Graph";
  if (pathname.includes("superadmin-patients"))
    return "Healthcare Professional Patients";
  if (pathname.includes("superadmin-session")) return "Patient Sessions";
  if (pathname.includes("superadmin-graph")) return "Patient Session Graph";
  return "Data Portal";
};

const NewLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = !useMediaQuery("(min-width:992px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { loginUserDetails, getLoginUserDetails } = useContext(UserContext);
  const [patchData, setPatchData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  // Dropdown menu anchor state
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);

  const loginType = getLoginType();
  const isSuperAdmin = loginType === "1";
  const menuItems = isSuperAdmin ? superAdminMenu : patientMenu;

  const handleLogout = () => {
    setIsLoading(true);
    logoutAndRedirect();
  };

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  // Auto-close the mobile sidebar after any navigation instead of leaving it
  // open until the user manually collapses it.
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDashboard = () => {
    navigate(isSuperAdmin ? "/admin-dashboard" : "/hp-dashboard");
  };

  useEffect(() => {
    if (loginUserDetails?.currentSuperAdminModel != null) {
      setPatchData(loginUserDetails?.currentSuperAdminModel);
    } else if (loginUserDetails?.currentHealthProfessionalModel != null) {
      setPatchData(loginUserDetails?.currentHealthProfessionalModel);
    } else if (loginUserDetails?.currentPatientModel != null) {
      setPatchData(loginUserDetails?.currentPatientModel);
    }
  }, [loginUserDetails]);

  useEffect(() => {
    // NewLayout is the layout route wrapping every protected route, so it
    // mounts (and this effect fires) for one render even on an
    // unauthenticated visit, before the nested ProtectedRoute's redirect
    // takes effect. Without this guard that fires a real GetCurrentUser call
    // with no token — if it ever lands before the redirect unmounts this
    // component, a 401 from the real backend trips the axios interceptor's
    // session-expired flow and force-redirects the page.
    if (!getAccessToken()) return;
    getLoginUserDetails();
  }, [getLoginUserDetails]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={`mainlayout ${isCollapsed ? "collapsed" : ""}`}>
      {isLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}

      {/* MOBILE BACKDROP */}
      {!isCollapsed && isMobile && (
        <div
          className="mobile-backdrop"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* SIDEBAR ON THE LEFT */}
      <aside
        className={`sidebar1 ${isCollapsed ? "collapsed" : "is-open"}`}
        aria-label="Main Navigation"
      >
        <button
          type="button"
          className="sidebar1_top"
          onClick={handleDashboard}
          style={{ cursor: "pointer" }}
        >
          <img src={logo} alt="Health-eTile Logo" className="sidebar1_logo" />
          {!isCollapsed && (
            <div className="sidebar1_brand">
              <span className="sidebar1_brand_name">Health-eTile</span>
              <span className="sidebar1_brand_tagline">WeBe Data Portal</span>
            </div>
          )}
        </button>

        <nav className="sidebar1_center" aria-label="Main Menu">
          <ul className="sidebar1_center_menu">
            {menuItems.map((item) => {
              const isActive =
                location.pathname === item.to ||
                location.pathname.startsWith(item.to + "/") ||
                (item.activePaths || []).some(
                  (path) =>
                    location.pathname === path ||
                    location.pathname.startsWith(path + "/"),
                );

              return (
                <li
                  key={item.to}
                  className={`sidebar1_center_menu_row ${isActive ? "active" : ""}`}
                >
                  <Tooltip
                    title={isCollapsed ? item.label : ""}
                    placement="right"
                    arrow
                  >
                    <button
                      type="button"
                      className={`sidebar1_center_menu_btn ${isActive ? "is-active" : ""}`}
                      onClick={() => navigate(item.to)}
                    >
                      <span className="sidebar1_center_menu_icon">
                        {item.icon}
                      </span>
                      {!isCollapsed && (
                        <span className="sidebar1_center_menu_label">
                          {item.label}
                        </span>
                      )}
                    </button>
                  </Tooltip>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar1_bottom">
          <Tooltip title={isCollapsed ? "Logout" : ""} placement="right" arrow>
            <button
              type="button"
              className="sidebar1_logout_btn"
              onClick={handleLogout}
            >
              <span className="sidebar1_logout_icon">
                <LogoutIcon />
              </span>
              {!isCollapsed && (
                <span className="sidebar1_logout_text">Logout</span>
              )}
            </button>
          </Tooltip>
        </div>
      </aside>

      {/* FLOATING TOGGLE BUTTON FOR DESKTOP */}
      {!isMobile && (
        <button
          type="button"
          className="sidebar1_toggle_floating"
          onClick={toggleSidebar}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <MenuOpenIcon
            className={`toggle_icon ${isCollapsed ? "collapsed" : ""}`}
          />
        </button>
      )}

      {/* MAIN CONTENT CONTAINER ON THE RIGHT */}
      <div className="content-container">
        {/* STICKY HEADER */}
        <header className="mainlayout_header">
          <div className="mainlayout_header_left">
            {isMobile && (
              <button
                type="button"
                className="sidebar1_toggle"
                onClick={toggleSidebar}
                aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
              >
                <MenuIcon />
              </button>
            )}
            <h1 className="header_page_title">
              {getPageTitle(location.pathname)}
            </h1>
          </div>

          <div className="mainlayout_header_Right">
            <button
              type="button"
              className="header_profile_pill"
              onClick={handleProfileMenuOpen}
            >
              <UserAvatar
                src={patchData?.imageUrl}
                name={patchData?.name}
                sx={{ width: 36, height: 36 }}
              />
              <div className="header_profile_details">
                <span className="header_profile_name">
                  {patchData?.name || "-"}
                </span>
                <span className="header_profile_role">
                  {isSuperAdmin ? "Super Admin" : "Healthcare Professional"}
                </span>
              </div>
              <KeyboardArrowDownIcon
                className={`header_profile_chevron ${isMenuOpen ? "rotated" : ""}`}
              />
            </button>

            {/* Profile Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                elevation: 3,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 8px 16px rgba(15, 23, 42, 0.08))",
                  mt: 1.5,
                  minWidth: 180,
                  borderRadius: "12px",
                  border: "1px solid #f1f5f9",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#334155",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    margin: "0 8px",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                      color: "#104581",
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={() => navigate("/profile-details")}>
                <ListItemIcon>
                  <PersonOutlineIcon
                    fontSize="small"
                    style={{ color: "#64748b" }}
                  />
                </ListItemIcon>
                Profile Details
              </MenuItem>
              <Divider sx={{ my: 1, borderColor: "#f1f5f9" }} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  color: "#ef4444 !important",
                  "&:hover": {
                    backgroundColor: "#fef2f2 !important",
                    color: "#ef4444 !important",
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutOutlinedIcon
                    fontSize="small"
                    style={{ color: "#ef4444" }}
                  />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </div>
        </header>

        {/* CONTENT RENDER AREA */}
        <main className="mainlayout_content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default NewLayout;
